import React, { useMemo, useState } from "react";
import { useGlobalData } from "../hooks/useGlobalHooks";
import { OptimizedNumberCell, LiveLTPCell } from "./OptimizedCell";
import BulkModifyModal from "./BulkModifyModal";
import { Pencil } from "lucide-react";
import { toast } from "react-hot-toast";

/**
 * Order Comparison Modal
 * Compares actual client orders with reference base quantities
 * Shows Symbol, Strategy, and Qty separately
 */
export function OrderComparisonModal({
  isOpen,
  onClose,
  clientData,
  orders,
  symbolStrategyBaseQty,
  onCancel, // Added prop for consistency
}) {
  const { users, indexMultipliers } = useGlobalData();

  // Selection & Modify State
  const [selectedOrderIds, setSelectedOrderIds] = useState(new Set());
  const [modifyModalOpen, setModifyModalOpen] = useState(false);
  const [ordersToModify, setOrdersToModify] = useState([]);
  // console.log("ORDERs-------------------------------------", orders)
  // console.log("🔍 Order Modal Debug:", {
  //   isOpen,
  //   clientData,
  //   ordersCount: orders?.length,
  //   usersCount: users?.length,
  //   indexMultipliersCount: indexMultipliers?.length,
  //   symbolStrategyBaseQtyCount: Object.keys(symbolStrategyBaseQty || {}).length,
  //   sampleOrder: orders?.[0],
  //   clientIdToMatch: clientData?.clientId,
  // });

  // Extract index from symbol
  function extractIndexFromSymbol(symbol) {
    if (!symbol) return null;

    const symbolUpper = symbol.toUpperCase();

    if (symbolUpper.includes("NIFTY") && symbolUpper.includes("BANK")) {
      return "banknifty";
    } else if (
      symbolUpper.includes("FINNIFTY") ||
      symbolUpper.includes("FIN NIFTY")
    ) {
      return "finnifty";
    } else if (
      symbolUpper.includes("MIDCPNIFTY") ||
      symbolUpper.includes("MIDCP")
    ) {
      return "midcpnifty";
    } else if (symbolUpper.includes("NIFTY")) {
      return "nifty";
    } else if (symbolUpper.includes("SENSEX") || symbolUpper.includes("BSX")) {
      return "sensex";
    } else if (symbolUpper.includes("BANKEX")) {
      return "bankex";
    } else {
      return null; // Ignore non-index symbols
    }
  }

  // Group orders by symbol+strategy for comparison
  const ordersBySymbolStrategy = useMemo(() => {
    if (!orders || !clientData?.clientId || !symbolStrategyBaseQty) return [];

    console.log(
      "📊 Building order comparison with symbolStrategyBaseQty:",
      symbolStrategyBaseQty
    );

    const rows = [];
    const processedKeys = new Set();

    // Get all buy orders for this client (tolerant side parsing)
    const clientOrders = orders.filter((o) => {
      const side = String(o.side || "")
        .trim()
        .toLowerCase();
      const cId = o.clientId || o.client_id;
      return (
        (side === "1" || side === "buy" || side === "b") &&
        cId === clientData.clientId
      );
    });
    
    if (clientData.clientId === "FAB01521") {
      console.log("Showing (filtered) orders for FAB01521:", clientOrders);
    }



    // Debug: Log ALL orders with their strategy field from backend
    // console.log("📋 [STRATEGY DEBUG] All client orders from backend:");

    // Process each order
    clientOrders.forEach((order) => {
      // Debug: Log orders with missing strategy


      // Unique key for ROW display: Symbol + Strategy + OrderID
      // This ensures every order gets its own row
      const rowUniqueKey = `${order.symbol}_${order.strategy || "NO_STRATEGY"}_${order.order_id || order.orderId}`;
      const aggregationKey = `${order.symbol}_${order.strategy || "NO_STRATEGY"}_${order.stop_price || 0}_${order.limit_price || 0}`;
      const lookupKey = `${order.symbol}_${order.strategy || "NO_STRATEGY"}`;

      // Skip if this specific order (row) is already processed
      if (processedKeys.has(rowUniqueKey)) return;
      processedKeys.add(rowUniqueKey);

      // Get base qty info for this symbol+strategy
      const baseQtyInfo = symbolStrategyBaseQty[lookupKey];

      if (!baseQtyInfo) {
        console.warn(`⚠️ No base qty found for ${aggregationKey}`);
        return;
      }

      // Get multiplier for this client and index
      const index = baseQtyInfo.index?.toUpperCase();
      console.log(
        `🎯 Looking up multiplier for client ${clientData.clientId}, index: '${index}'`
      );
      const multiplier = getMultiplier(index);

      // Calculate expected qty
      const baseQty = baseQtyInfo.baseQty;
      const expectedQty = multiplier * baseQty;

      // Sum actual qty for this client's KEY (Symbol+Strategy)
      // We sum ALL orders matching the aggregation key, not just this one
      let actualQty = 0;
      clientOrders.forEach((o) => {
        const oKey = `${o.symbol}_${o.strategy || "NO_STRATEGY"}_${o.stop_price || 0}_${o.limit_price || 0}`;
        if (oKey === aggregationKey) {
          actualQty += Math.abs(parseFloat(o.qty || 0));
        }
      });

      rows.push({
        symbol: order.symbol,
        strategy: order.strategy || "N/A",
        index: baseQtyInfo.index,
        baseQty: baseQty,
        multiplier: multiplier,
        orderQty: Math.abs(parseFloat(order.qty || 0)), // Individual order quantity
        expectedQty: expectedQty, // Total expected
        actualQty: actualQty, // Total actual
        stop_price: order.stop_price,
        limit_price: order.limit_price,
        order_id: order.order_id || order.orderId || "N/A",
        orderdatetime: order.orderdatetime || order.orderDateTime || "N/A",
        match: actualQty === expectedQty,
        clientsTrading: baseQtyInfo.clients,
        strike: extractStrike(order.symbol),
        status: order.status || "",
        originalOrder: order,
      });
    });

    if (clientData.clientId === "FAB01521") {
      console.log("Final showing rows for FAB01521:", rows);
    }

    return rows;
  }, [orders, clientData, symbolStrategyBaseQty]);

  // -- FILTER LOGIC --
  const [selectedAsset, setSelectedAsset] = useState("ALL");

  const uniqueAssets = useMemo(() => {
    const assets = new Set(ordersBySymbolStrategy.map(r => r.index).filter(Boolean));
    return ["ALL", ...Array.from(assets).sort()];
  }, [ordersBySymbolStrategy]);

  const filteredRows = useMemo(() => {
    if (selectedAsset === "ALL") return ordersBySymbolStrategy;
    return ordersBySymbolStrategy.filter(r => r.index === selectedAsset);
  }, [ordersBySymbolStrategy, selectedAsset]);

  // Extract strike from symbol
  function extractStrike(symbol) {
    const match = symbol?.match(/(\d{5,6})(CE|PE)$/);
    return match ? match[1] : "N/A";
  }

  // Format price with commas and 2 decimals
  function formatPrice(num) {
    if (num === undefined || num === null || num === 0) return "-";
    return Number(num).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }

  // Get multiplier for an index
  function getMultiplier(index) {
    if (!indexMultipliers || !clientData?.clientId) {
      console.log("⚠️ Cannot get multiplier:", {
        hasIndexMultipliers: !!indexMultipliers,
        hasClientId: !!clientData?.clientId,
        indexMultipliersLength: indexMultipliers?.length,
      });
      return "N/A";
    }

    console.log("🔍 Searching for multiplier:", {
      searchClientId: clientData.clientId,
      searchIndex: index,
      totalMultipliers: indexMultipliers.length,
      sampleMultiplier: indexMultipliers[0],
    });

    const matches = indexMultipliers.filter(
      (m) => m.client_id === clientData.clientId && m.index === index
    );

    const mult =
      matches.find((m) => ["dhan_xts", "dhan_xts_anurag"].includes((m.broker || "").toLowerCase())) ||
      matches[0];



    return mult ? parseFloat(mult.multiplier) : "N/A";
  }

  // Format date to IST
  function formatOrderTime(dt) {
    if (!dt || dt === "N/A") return "-";
    try {
      const date = new Date(dt);
      return date.toLocaleString("en-IN", {
        timeZone: "Asia/Kolkata",
        day: "2-digit",
        month: "short",
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
        hour12: false,
      });
    } catch (e) {
      return dt;
    }
  }

  // Selection Handlers
  const toggleOrderSelection = (orderId) => {
    const next = new Set(selectedOrderIds);
    if (next.has(orderId)) next.delete(orderId);
    else next.add(orderId);
    setSelectedOrderIds(next);
  };

  const handleBulkModify = () => {
    const toMod = filteredRows
      .filter(r => selectedOrderIds.has(r.order_id))
      .map(r => ({
        ...r.originalOrder,
        expectedQty: r.expectedQty // Pass expected for "Sync" feature
      }));
    
    if (toMod.length === 0) {
      toast.error("Select orders to modify");
      return;
    }
    setOrdersToModify(toMod);
    setModifyModalOpen(true);
  };

  const handleSingleModify = (row) => {
    setOrdersToModify([{
      ...row.originalOrder,
      expectedQty: row.expectedQty
    }]);
    setModifyModalOpen(true);
  };

  if (!isOpen) return null;

  return (
    <>
      <div
        className="fixed inset-0 bg-black/20 flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <div
          className="bg-[#1a1d29] rounded-lg shadow-xl max-w-7xl w-full max-h-[80vh] overflow-hidden flex flex-col border border-gray-700/50"
          onClick={(e) => e.stopPropagation()}
        >
        {/* Header */}
        <div className="bg-[#14161f] px-6 py-4 border-b border-gray-700 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <h2 className="text-xl font-bold text-white">Order Comparison</h2>
            {selectedOrderIds.size > 0 && (
              <button
                onClick={handleBulkModify}
                className="bg-blue-600 hover:bg-blue-500 text-white text-xs font-bold px-3 py-1.5 rounded transition-all shadow-lg flex items-center gap-2"
              >
                Modify Selected ({selectedOrderIds.size})
              </button>
            )}
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white transition-colors text-2xl font-bold leading-none"
          >
            ×
          </button>
        </div>

        {/* Client Info */}
        <div className="bg-[#1a1d29] px-6 py-3 border-b border-gray-700/50">
          <div className="flex items-center gap-4 text-sm">
            <span className="text-gray-400">Client:</span>
            <span className="text-white font-semibold">
              {clientData?.clientName || clientData?.clientId}
            </span>
            <span className="text-gray-400">({clientData?.clientId})</span>
          </div>
        </div>

        {/* Debug Bar */}
        {/* <div className="bg-[#0f1117] px-6 py-2 border-b border-gray-700/30">
          <div className="flex flex-wrap gap-4 text-xs text-gray-400">
            <span>📊 Total Orders: {orders?.length || 0}</span>
            <span>👤 Users: {users?.length || 0}</span>
            <span>📈 Index Multipliers: {indexMultipliers?.length || 0}</span>
            <span>📝 Rows: {ordersBySymbolStrategy.length}</span>
          </div>
        </div> */}

        {/* Content */}
        <div className="flex-1 overflow-auto">
          {ordersBySymbolStrategy.length === 0 ? (
            <div className="text-center py-8 px-6">
              <p className="text-gray-400 mb-4">
                No orders found for comparison
              </p>
              <div className="text-left bg-red-900/20 border border-red-700/30 rounded p-4 max-w-2xl mx-auto text-xs">
                <p className="text-red-400 font-semibold mb-2">Debug Info:</p>
                <p className="text-gray-300">
                  • Total orders available: {orders?.length || 0}
                </p>
                <p className="text-gray-300">
                  • Client ID: {clientData?.clientId || "N/A"}
                </p>
                <p className="text-gray-300">
                  • Client Name: {clientData?.clientName || "N/A"}
                </p>
                <p className="text-gray-300">
                  • Symbol+Strategy base qty entries:{" "}
                  {Object.keys(symbolStrategyBaseQty || {}).length}
                </p>
                <p className="text-yellow-400 mt-2">
                  💡 Open browser console (F12) for detailed logs
                </p>
              </div>
            </div>
          ) : (
            <div className="bg-[#14161f] overflow-hidden">
              <div className="overflow-auto max-h-[calc(80vh-280px)]">
                <table className="w-full text-sm">
                  <thead className="bg-[#0f1117] sticky top-0 z-10">
                    <tr className="border-b border-gray-700/30">
                      <th className="w-8 px-2 text-center">
                        <input 
                          type="checkbox"
                          className="w-4 h-4 rounded border-gray-700 bg-gray-900 text-blue-600 focus:ring-blue-500"
                          checked={filteredRows.length > 0 && filteredRows.every(r => selectedOrderIds.has(r.order_id))}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setSelectedOrderIds(new Set(filteredRows.map(r => r.order_id)));
                            } else {
                              setSelectedOrderIds(new Set());
                            }
                          }}
                        />
                      </th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium">
                        Order ID
                      </th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium">
                        Order Time
                      </th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium">
                        <div className="flex items-center gap-2">
                          <span>Index</span>
                          <select
                            value={selectedAsset}
                            onChange={(e) => setSelectedAsset(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            className="bg-[#1a1d29] border border-gray-700 text-gray-300 text-xs rounded px-1 py-0.5 focus:outline-none focus:border-blue-500"
                          >
                            {uniqueAssets.map(asset => (
                              <option key={asset} value={asset}>
                                {asset === "ALL" ? "All" : asset.toUpperCase()}
                              </option>
                            ))}
                          </select>
                        </div>
                      </th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium">
                        Symbol
                      </th>
                      <th className="text-left py-3 px-4 text-gray-400 font-medium">
                        Strategy
                      </th>
                      <th className="text-center py-3 px-4 text-gray-400 font-medium">
                        Strike
                      </th>
                      <th className="text-center py-3 px-4 text-gray-400 font-medium">
                        Stop Price
                      </th>
                      <th className="text-center py-3 px-4 text-gray-400 font-medium whitespace-nowrap">
                        Limit Price
                      </th>
                      <th className="text-center py-3 px-4 text-gray-400 font-medium whitespace-nowrap">
                        LTP
                      </th>
                      <th className="text-center py-3 px-4 text-gray-400 font-medium">
                        Base Qty (1x)
                      </th>
                      <th className="text-center py-3 px-4 text-gray-400 font-medium">
                        Multiplier
                      </th>
                      <th className="text-center py-3 px-4 text-gray-400 font-medium">
                        Order Qty
                      </th>
                      <th className="text-center py-3 px-4 text-gray-400 font-medium">
                        Expected Total
                      </th>
                      <th className="text-center py-3 px-4 text-gray-400 font-medium">
                        Actual Total
                      </th>
                      <th className="text-center py-3 px-4 text-gray-400 font-medium">
                        Match
                      </th>
                      <th className="text-center py-3 px-4 text-gray-400 font-medium">
                        Action
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRows.map((row, idx) => {
                      const isMatch = row.actualQty === row.expectedQty;
                      const qtyColor = isMatch
                        ? "text-green-400"
                        : "text-red-400";

                      return (
                        <tr
                          key={idx}
                          className="border-b border-gray-800/30 hover:bg-[#1a1d29] transition-colors"
                        >
                          <td className="px-2 text-center">
                            <input 
                              type="checkbox"
                              className="w-3.5 h-3.5 rounded border-gray-800 bg-gray-950 text-blue-600 focus:ring-blue-500"
                              checked={selectedOrderIds.has(row.order_id)}
                              onChange={() => toggleOrderSelection(row.order_id)}
                            />
                          </td>
                          <td className="py-2.5 px-4 text-gray-300 text-xs font-mono">
                            {row.order_id}
                          </td>
                          <td className="py-2.5 px-4 text-gray-300 text-xs">
                            {formatOrderTime(row.orderdatetime)}
                          </td>
                          <td className="py-2.5 px-4">
                            <span className="text-blue-400 text-xs font-semibold">
                              {row.index?.toUpperCase()}
                            </span>
                          </td>
                          <td className="py-2.5 px-4 text-gray-300 text-xs font-mono">
                            {row.symbol}
                          </td>
                          <td className="py-2.5 px-4 text-gray-400 text-xs">
                            {row.strategy}
                          </td>
                          <td className="py-2.5 px-4 text-center text-gray-300">
                            {row.strike}
                          </td>
                          <td className="py-2.5 px-4 text-center text-gray-300">
                            {row.stop_price}
                          </td>
                          <td className="py-2.5 px-4 text-center text-gray-300">
                            {row.limit_price}
                          </td>
                          <td className="py-2.5 px-4 text-center text-blue-400 font-medium">
                            <LiveLTPCell symbol={row.symbol} formatFn={formatPrice} />
                          </td>
                          <td className="py-2.5 px-4 text-center text-white font-medium">
                            {row.baseQty}
                          </td>
                          <td className="py-2.5 px-4 text-center text-purple-400 font-medium">
                            {row.multiplier}x
                          </td>
                          <td className="py-2.5 px-4 text-center text-gray-300 font-medium">
                            {row.orderQty}
                          </td>
                          <td className="py-2.5 px-4 text-center text-yellow-400 font-medium">
                            {row.expectedQty}
                          </td>
                          <td
                            className={`py-2.5 px-4 text-center font-bold ${qtyColor}`}
                          >
                            {row.actualQty}
                          </td>
                          <td className="py-2.5 px-4 text-center">
                            {isMatch ? (
                              <span className="text-green-400">✓</span>
                            ) : (
                              <span className="text-red-400">✗</span>
                            )}
                          </td>
                          <td className="py-2.5 px-4 text-center">
                            {["pending", "open", "trigger pending", "not sent"].includes((row.status || "").toLowerCase()) && row.originalOrder ? (
                               <div className="flex items-center justify-center gap-1">
                                <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleSingleModify(row);
                                    }}
                                    className="p-1 text-blue-400 hover:text-white hover:bg-blue-600/20 border border-blue-800/50 rounded transition-all"
                                    title="Modify Order"
                                  >
                                    <Pencil className="w-3.5 h-3.5" />
                                  </button>
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (onCancel) {
                                        onCancel([row.originalOrder]);
                                      } else if (typeof window.onCancelOrder === 'function') {
                                        window.onCancelOrder([row.originalOrder]);
                                      }
                                    }}
                                    className="px-2 py-1 text-[10px] bg-red-900/30 hover:bg-red-800/40 text-red-200 border border-red-800/50 rounded transition-colors"
                                  >
                                    Cancel
                                  </button>
                               </div>
                            ) : (
                              <span className="text-gray-500 text-[10px]">-</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      </div>
      </div>
      
      <BulkModifyModal 
        isOpen={modifyModalOpen}
        onClose={() => setModifyModalOpen(false)}
        orders={ordersToModify}
        onSuccess={() => {
          setSelectedOrderIds(new Set());
        }}
      />
    </>
  );
}
